import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ModelService } from "@/services/ModelService";

const AgentTypesContext = createContext({
    agents: [],
    recruiterAgents: [],
    salesManagerAgents: [],
    hasRecruiterAgents: false,
    hasSalesManagerAgents: false,
    isLoading: true,
    refresh: () => {},
});

export const AgentTypesProvider = ({ children }) => {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const load = useCallback(() => {
        setIsLoading(true);
        return ModelService.getAll(1, 100)
            .then((response) => setAgents(response?.items ?? []))
            .catch(() => setAgents([]))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Agents saved before AgentType existed come back with type: null (see the backend's
    // AgentType doc comment) but were exclusively recruitment agents -- SalesManager is a
    // newer product line -- so treat an untyped agent as a recruiter, never as sales.
    const recruiterAgents = useMemo(() => agents.filter((a) => a.type === "Recruiter" || a.type == null), [agents]);
    const salesManagerAgents = useMemo(() => agents.filter((a) => a.type === "SalesManager"), [agents]);

    const value = useMemo(() => ({
        agents,
        recruiterAgents,
        salesManagerAgents,
        hasRecruiterAgents: recruiterAgents.length > 0,
        hasSalesManagerAgents: salesManagerAgents.length > 0,
        isLoading,
        refresh: load,
    }), [agents, recruiterAgents, salesManagerAgents, isLoading, load]);

    return (
        <AgentTypesContext.Provider value={value}>
            {children}
        </AgentTypesContext.Provider>
    );
};

export const useAgentTypes = () => useContext(AgentTypesContext);
